import { useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { LogOut, Sun, Moon, LayoutDashboard, User, Settings } from 'lucide-react';
import { useState } from 'react';

export const DashboardLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const { darkMode, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [loggingOut, setLoggingOut] = useState(false);

    const handleLogout = async () => {
        setLoggingOut(true);
        try {
            await logout();
            navigate('/login');
        } finally {
            setLoggingOut(false);
        }
    };

    return (
        <div className="min-h-screen">
            {/* Header */}
            <header className="glass sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-glow" style={{ background: 'linear-gradient(135deg, #06B6D4, #3B82F6)' }}>
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 2L2 7l8 5 8-5-8-5z" />
                                    <path d="M2 17l8 5 8-5M2 12l8 5 8-5" opacity="0.7" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{import.meta.env.VITE_APP_NAME || "Snap Solver AI"}</h1>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Command Center</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-4">
                            <button
                                onClick={toggleTheme}
                                className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                            >
                                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                            </button>

                            <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-glow" style={{ background: 'linear-gradient(135deg, #06B6D4, #3B82F6)' }}>
                                    {user?.username?.[0]?.toUpperCase() || 'U'}
                                </div>
                                <div className="hidden sm:block">
                                    <div className="text-xs sm:text-sm font-medium truncate max-w-[80px] md:max-w-[120px]">{user?.username}</div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400 hidden md:block truncate max-w-[120px]">{user?.email}</div>
                                </div>
                            </div>

                            <button
                                onClick={handleLogout}
                                disabled={loggingOut}
                                className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 transition-colors disabled:opacity-50"
                                title={loggingOut ? 'Logging out...' : 'Logout'}
                            >
                                <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Navigation Menu */}
            <nav className="glass border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
                    <div className="flex gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide">
                        <NavLink
                            to="/dashboard"
                            className={({ isActive }) =>
                                `flex-shrink-0 px-3 sm:px-4 py-3 font-medium text-xs sm:text-sm md:text-base transition-all flex items-center gap-1.5 sm:gap-2 border-b-2 ${isActive
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`
                            }
                        >
                            <LayoutDashboard className="w-4 h-4" />
                            <span>Dashboard</span>
                        </NavLink>

                        <NavLink
                            to="/profile"
                            className={({ isActive }) =>
                                `flex-shrink-0 px-3 sm:px-4 py-3 font-medium text-xs sm:text-sm md:text-base transition-all flex items-center gap-1.5 sm:gap-2 border-b-2 ${isActive
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`
                            }
                        >
                            <User className="w-4 h-4" />
                            <span>Profile</span>
                        </NavLink>

                        <NavLink
                            to="/account"
                            className={({ isActive }) =>
                                `flex-shrink-0 px-3 sm:px-4 py-3 font-medium text-xs sm:text-sm md:text-base transition-all flex items-center gap-1.5 sm:gap-2 border-b-2 ${isActive
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`
                            }
                        >
                            <Settings className="w-4 h-4" />
                            <span>Account</span>
                        </NavLink>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">{children}</main>
        </div>
    );
};
